import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-card/50 backdrop-blur-sm rounded-lg shadow-lg p-8 space-y-6">
          <h1 className="text-4xl font-bold text-primary mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last Updated: October 31, 2025</p>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">1. Introduction</h2>
            <p className="text-foreground/90 leading-relaxed">
              Cut Previews ("we," "us," or "our") operates the Cut Previews mobile application and website 
              (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and 
              safeguard your information when you use our AI-powered hairstyle visualization service.
            </p>
            <p className="text-foreground/90 leading-relaxed">
              By using our Service, you agree to the collection and use of information in accordance with this policy. 
              If you do not agree with our policies and practices, please do not use our Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-foreground mt-4">2.1 Personal Information</h3>
            <p className="text-foreground/90 leading-relaxed">We collect the following personal information:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li><strong>Account Information:</strong> Email address, full name, and password (encrypted)</li>
              <li><strong>Profile Information:</strong> Optional ethnicity information for improved AI results</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store full payment card details)</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mt-4">2.2 Photos and Images</h3>
            <p className="text-foreground/90 leading-relaxed">
              <strong>User-Uploaded Photos:</strong> When you use our hairstyle try-on feature, you upload photos of yourself. 
              These images are:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li>Stored securely in our cloud infrastructure</li>
              <li>Processed by AI to generate hairstyle visualizations</li>
              <li>Associated with your account for history and regeneration purposes</li>
              <li>Only accessible to you and our authorized systems</li>
              <li>Not shared with third parties except as necessary for AI processing</li>
              <li>Not used for training AI models without explicit consent</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mt-4">2.3 Usage Information</h3>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li><strong>Device Information:</strong> Device type, operating system, browser type</li>
              <li><strong>Usage Data:</strong> Features accessed, generation history, timestamps</li>
              <li><strong>Analytics:</strong> App performance, crash reports, interaction patterns</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mt-4">2.4 Cookies and Local Storage</h3>
            <p className="text-foreground/90 leading-relaxed">
              We use essential cookies and local storage for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li>Authentication and session management</li>
              <li>Remembering your preferences</li>
              <li>Analyzing usage patterns to improve the Service</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">3. How We Use Your Information</h2>
            <p className="text-foreground/90 leading-relaxed">We use collected information for:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li><strong>Service Provision:</strong> Processing photos, generating hairstyle visualizations, managing your account</li>
              <li><strong>AI Processing:</strong> Using artificial intelligence to apply hairstyles to your photos</li>
              <li><strong>Payment Processing:</strong> Managing subscriptions and processing payments via Stripe</li>
              <li><strong>Communication:</strong> Sending account-related notifications and updates</li>
              <li><strong>Improvement:</strong> Analyzing usage to enhance features and user experience</li>
              <li><strong>Security:</strong> Detecting and preventing fraud, abuse, and technical issues</li>
              <li><strong>Legal Compliance:</strong> Fulfilling legal obligations and enforcing our terms</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">4. AI and Image Processing</h2>
            <p className="text-foreground/90 leading-relaxed">
              <strong>AI Processing Disclosure:</strong> Our Service uses artificial intelligence to process your photos 
              and generate hairstyle visualizations. This involves:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li>Analyzing facial features in uploaded photos</li>
              <li>Applying selected hairstyles using AI image generation</li>
              <li>Processing images through third-party AI services (with appropriate data protections)</li>
              <li>Temporary processing in memory that is not retained after generation</li>
            </ul>
            <p className="text-foreground/90 leading-relaxed mt-4">
              <strong>Important:</strong> We do not use your photos to train AI models. Your images are used solely 
              to provide the hairstyle visualization service you requested.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">5. Data Sharing and Third Parties</h2>
            <p className="text-foreground/90 leading-relaxed">We share information with:</p>
            
            <h3 className="text-xl font-semibold text-foreground mt-4">5.1 Service Providers</h3>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li><strong>Cloud Infrastructure:</strong> Secure storage and database services</li>
              <li><strong>AI Services:</strong> Third-party AI providers for image generation (under strict data processing agreements)</li>
              <li><strong>Payment Processing:</strong> Stripe for secure payment handling</li>
              <li><strong>Email Services:</strong> For authentication and notifications</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mt-4">5.2 Legal Requirements</h3>
            <p className="text-foreground/90 leading-relaxed">
              We may disclose information if required by law, court order, or to protect rights, property, or safety.
            </p>

            <h3 className="text-xl font-semibold text-foreground mt-4">5.3 Business Transfers</h3>
            <p className="text-foreground/90 leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred. 
              We will notify you before your information is transferred and becomes subject to a different privacy policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">6. Data Security</h2>
            <p className="text-foreground/90 leading-relaxed">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li><strong>Encryption:</strong> Data encrypted in transit (HTTPS/TLS) and at rest</li>
              <li><strong>Access Controls:</strong> Strict authentication and authorization systems</li>
              <li><strong>Secure Storage:</strong> Photos stored in protected cloud storage with access restrictions</li>
              <li><strong>Regular Audits:</strong> Ongoing security reviews and updates</li>
              <li><strong>Password Protection:</strong> Passwords hashed using industry-standard algorithms</li>
            </ul>
            <p className="text-foreground/90 leading-relaxed mt-4">
              <strong>Note:</strong> While we strive to protect your information, no method of transmission over the internet 
              or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">7. Your Privacy Rights</h2>
            
            <h3 className="text-xl font-semibold text-foreground mt-4">7.1 General Rights</h3>
            <p className="text-foreground/90 leading-relaxed">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Objection:</strong> Object to certain data processing activities</li>
              <li><strong>Restriction:</strong> Request restricted processing of your data</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mt-4">7.2 GDPR Rights (EU/EEA Users)</h3>
            <p className="text-foreground/90 leading-relaxed">
              If you are in the European Economic Area, you have additional rights under GDPR:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li>Right to withdraw consent at any time</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
              <li>Right to know the legal basis for processing your data</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mt-4">7.3 CCPA Rights (California Users)</h3>
            <p className="text-foreground/90 leading-relaxed">
              California residents have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li>Know what personal information is collected, used, shared, or sold</li>
              <li>Delete personal information held by businesses</li>
              <li>Opt-out of the sale of personal information (we do not sell your information)</li>
              <li>Non-discrimination for exercising CCPA rights</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mt-4">7.4 Exercising Your Rights</h3>
            <p className="text-foreground/90 leading-relaxed">
              To exercise any of these rights, please contact us at the information provided below. 
              We will respond within 30 days of your request.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">8. Data Retention</h2>
            <p className="text-foreground/90 leading-relaxed">We retain your information as follows:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li><strong>Account Information:</strong> Until you delete your account, plus 30 days for backup purposes</li>
              <li><strong>Photos and Generations:</strong> Until you delete them or your account, plus 30 days for backup purposes</li>
              <li><strong>Payment Records:</strong> 7 years for tax and legal compliance</li>
              <li><strong>Usage Analytics:</strong> Aggregated data retained indefinitely (anonymized)</li>
            </ul>
            <p className="text-foreground/90 leading-relaxed mt-4">
              When you delete your account, we will delete or anonymize your personal information within 30 days, 
              except where retention is required by law.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">9. Children's Privacy</h2>
            <p className="text-foreground/90 leading-relaxed">
              Our Service is not intended for users under the age of 13 (or 16 in the EU). We do not knowingly 
              collect personal information from children. If you are a parent or guardian and believe your child 
              has provided us with personal information, please contact us immediately. We will delete such information 
              from our systems within 30 days.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">10. International Data Transfers</h2>
            <p className="text-foreground/90 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws. When we transfer data internationally, 
              we use appropriate safeguards such as:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li>Standard contractual clauses approved by the European Commission</li>
              <li>Data processing agreements with third-party providers</li>
              <li>Compliance with applicable data protection frameworks</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">11. Changes to This Policy</h2>
            <p className="text-foreground/90 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li>Posting the new Privacy Policy on this page</li>
              <li>Updating the "Last Updated" date at the top</li>
              <li>Sending an email notification for significant changes (if you have an account)</li>
            </ul>
            <p className="text-foreground/90 leading-relaxed mt-4">
              Your continued use of the Service after changes constitute acceptance of the updated policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">12. Contact Us</h2>
            <p className="text-foreground/90 leading-relaxed">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="bg-muted/50 rounded-lg p-6 mt-4">
              <p className="text-foreground font-semibold mb-2">Cut Previews</p>
              <p className="text-foreground/90">Email: privacy@cutpreviews.com</p>
              <p className="text-foreground/90 mt-4">
                For GDPR-related inquiries (EU users), please use subject line: "GDPR Request"
              </p>
              <p className="text-foreground/90">
                For CCPA-related inquiries (California users), please use subject line: "CCPA Request"
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">13. Google Play Store Compliance</h2>
            <p className="text-foreground/90 leading-relaxed">
              This app complies with Google Play's data safety requirements:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/90 ml-4">
              <li><strong>Data Collection:</strong> We collect personal information, photos, and usage data as described above</li>
              <li><strong>Data Sharing:</strong> We share data with service providers for app functionality only</li>
              <li><strong>Security:</strong> All data is encrypted in transit using industry-standard protocols</li>
              <li><strong>Data Deletion:</strong> Users can request deletion of their data through account settings or by contacting us</li>
              <li><strong>AI-Generated Content:</strong> We comply with Google Play's AI-generated content policies and do not generate 
                content that violates content policies</li>
            </ul>
          </section>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground">
              This Privacy Policy was last updated on October 31, 2025. Previous versions are available upon request.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;